import { Fragment, useCallback, useState } from 'react'
import { isEmpty } from 'lodash'
import { Formik, Field, Form, FormikErrors } from 'formik'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import Modal from 'components/Modal'
import TextBox from 'components/TextBox'
import SelectDropdown from 'components/SelectDropdown'
import Loader from 'components/Loader'

import { useCreateAccountMutation } from 'store/porticoApi/accounts'
import { useGetRolesQuery } from 'store/porticoApi/roles'
import { useCreateRoleAssigmentMutation } from 'store/porticoApi/roleAssignments'
import { useGetVenuesQuery } from 'store/porticoApi/venues'
import { filterRolesByAppTypeAndName } from 'utils/accounts/filterRolesByAppTypeAndName'
import { TAddAccountFormValues, TCreateAccount } from 'types/accounts'
import { TRole } from 'types/roles'
import { setAddAccountRoleAssignment } from 'utils/accounts/setAddAccountRoleAssignments'

const AddAccount = () => {
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const { data: venues, isFetching: isFetchingVenues } = useGetVenuesQuery()
  const { data: roles, isFetching: isFetchingRoles } = useGetRolesQuery()

  const [createUser] = useCreateAccountMutation()
  const [createRoleAssigment] = useCreateRoleAssigmentMutation()

  const navigate = useNavigate()

  const isFetching = isFetchingVenues || isFetchingRoles

  const initialValues: TAddAccountFormValues = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    roleAssignments: [],
  }

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    email: Yup.string().required('Required'),
  })

  const disableUpdateButton = useCallback(
    (dirty: boolean, errors: FormikErrors<TAddAccountFormValues>) => {
      const disableButton = !dirty || (showValidationErrors && !isEmpty(errors))

      return disableButton
    },
    [showValidationErrors]
  )

  const onClickSaveButton = useCallback(() => {
    setShowValidationErrors(true)
  }, [])

  const transformRolesAsSelectOptions = filterRolesByAppTypeAndName(roles as TRole[])?.map(
    ({ name, uuid }) => ({
      name,
      uuid,
    })
  )

  const onCloseModal = () => navigate('/accounts')

  const onSubmit = (values: TAddAccountFormValues) => {
    const { firstName, lastName, email, phoneNumber, roleAssignments } = values

    const createAccountValues: TCreateAccount = {
      firstName,
      lastName,
      email,
      phoneNumber,
    }

    // Transform structure of role assignments in order to send request createRoleAssignment
    const transformRoleAssignments = (userId: string) =>
      roleAssignments?.map(
        ({ userRoleId, venueId }): { userRoleId: string; venueId: string; userId: string } => ({
          userRoleId,
          venueId,
          userId,
        })
      )

    createUser(createAccountValues)
      .unwrap()
      .then((res) => {
        createRoleAssigment(transformRoleAssignments(res.id))
          .unwrap()
          .then(() => {
            navigate('/accounts')
            toast.success('Success!')
          })
      })
  }

  if (isFetching) {
    return <Loader />
  }

  return (
    <Modal title="Add Account" onClose={onCloseModal} className="-narrow">
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ dirty, errors, setFieldValue, values }) => (
            <Form className="item-settings">
              <section className="item-settings__section ">
                <div className="content-wrapper">
                  <Field
                    component={TextBox}
                    label="First Name"
                    name="firstName"
                    wrapperClassName="-space-right"
                    showValidationErrors={showValidationErrors}
                    onChange={(value: string) => setFieldValue('firstName', value)}
                    value={values?.firstName}
                    error={errors?.firstName}
                  />
                  <Field
                    component={TextBox}
                    label="Last Name"
                    name="lastName"
                    showValidationErrors={showValidationErrors}
                    onChange={(value: string) => setFieldValue('lastName', value)}
                    value={values?.lastName}
                    error={errors?.lastName}
                  />
                </div>
                <div className="text-field">
                  <Field
                    component={TextBox}
                    label="Email Address"
                    name="email"
                    showValidationErrors={showValidationErrors}
                    onChange={(value: string) => setFieldValue('email', value)}
                    value={values?.email}
                    error={errors?.email}
                  />
                </div>
                <div className="text-field">
                  <Field
                    component={TextBox}
                    label="Phone Number"
                    name="phoneNumber"
                    onChange={(value: string) => setFieldValue('phoneNumber', value)}
                    value={values?.phoneNumber}
                    error={errors?.phoneNumber}
                  />
                </div>
                <div className="item-settings__border-break"></div>
                <h3 className="header">Roles</h3>
                <div className="row">
                  {venues?.map(({ venueUuid, venueName }) => (
                    <Fragment key={venueUuid}>
                      <div className="content-wrapper">
                        <span className="content-header">{venueName}</span>
                        <Field
                          component={SelectDropdown}
                          options={transformRolesAsSelectOptions}
                          onChange={(value: { name: string; uuid: string }) =>
                            setAddAccountRoleAssignment(value, values, venueUuid, setFieldValue)
                          }
                          shorter
                        />
                      </div>
                      <div className="item-settings__border-break"></div>
                    </Fragment>
                  ))}
                </div>
              </section>
              <div className="btn-wrapper">
                <button
                  className="btn -update"
                  onClick={onClickSaveButton}
                  disabled={disableUpdateButton(dirty, errors)}
                  type="submit"
                >
                  Save
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export default AddAccount
